/** @file Constant-pixel-size gizmo handles in screen space for the translate, rotate and scale tools. */
import { affine } from '@pose2d/math/affine';
import { vec2 } from '@pose2d/math/vec2';
import type { Vec2 } from '@pose2d/math/types';
import type { Camera } from '@pose2d/geometry/types';
import { unitAt } from './_internal/unitAt';
import { HANDLE_PX } from './handlePx';

/** Viewport tools that show a transform gizmo. */
export type GizmoTool = 'translate' | 'rotate' | 'scale';

/** Every grabbable part of a gizmo. */
export type GizmoPart = 'free' | 'axisX' | 'axisY' | 'ring' | 'uniform' | 'scaleX' | 'scaleY';

/** One handle in screen pixels: a disc, a thick segment (radius = half-width) or a ring band. */
export type GizmoHandle =
  | { part: GizmoPart; shape: 'disc'; center: Vec2; radius: number }
  | { part: GizmoPart; shape: 'segment'; a: Vec2; b: Vec2; radius: number }
  | { part: GizmoPart; shape: 'ring'; center: Vec2; radius: number; band: number };

/** Where the gizmo sits: world origin, world angle of its X axis (degrees, CCW, y-up) and the camera. */
export interface GizmoFrame {
  camera: Camera;
  origin: Vec2;
  rotation: number;
}

/** Screen-space unit direction of the world direction at deg degrees. */
function screenDir(camera: Camera, deg: number): Vec2 {
  const u = unitAt(deg, [0, 0]);
  const d = affine.applyVec(camera.worldToPixel, u[0], u[1], u);
  return vec2.normalize(d, d);
}

/** End point of an axis of HANDLE_PX.axis pixels from o along unit direction d. */
function tip(o: Vec2, d: Vec2): Vec2 {
  return [o[0] + d[0] * HANDLE_PX.axis, o[1] + d[1] * HANDLE_PX.axis];
}

/** Handles of the tool in hit-priority order (earlier handles win overlaps); sizes never depend on zoom. */
export function gizmoHandles(tool: GizmoTool, frame: GizmoFrame): GizmoHandle[] {
  const o = affine.apply(frame.camera.worldToPixel, frame.origin[0], frame.origin[1], [0, 0]);
  const x = tip(o, screenDir(frame.camera, frame.rotation));
  const y = tip(o, screenDir(frame.camera, frame.rotation + 90));
  switch (tool) {
    case 'translate':
      return [
        { part: 'free', shape: 'disc', center: [o[0], o[1]], radius: HANDLE_PX.disc },
        { part: 'axisX', shape: 'segment', a: [o[0], o[1]], b: x, radius: HANDLE_PX.band },
        { part: 'axisY', shape: 'segment', a: [o[0], o[1]], b: y, radius: HANDLE_PX.band },
      ];
    case 'rotate':
      return [{ part: 'ring', shape: 'ring', center: o, radius: HANDLE_PX.ring, band: HANDLE_PX.ringBand }];
    case 'scale':
      return [
        { part: 'uniform', shape: 'disc', center: o, radius: HANDLE_PX.disc },
        { part: 'scaleX', shape: 'disc', center: x, radius: HANDLE_PX.disc },
        { part: 'scaleY', shape: 'disc', center: y, radius: HANDLE_PX.disc },
      ];
  }
}
